import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { STATUS, TYPE } from "../../design/ui";
import { statusKey, typeKey } from "../../design/domain";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import {
  MdAdd,
  MdCheck,
  MdCircle,
  MdErrorOutline,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 專案詳情 › 工項分頁（設計稿 #screen-project 的下半段，el-pm-template_4.html L797-L855）
 *
 * 舊版這一段有：5 個狀態膠囊、8 種排序＋升降冪鈕、卡片／列表兩種檢視、7 欄的表格。
 * 一個只想知道「這個專案現在卡在哪」的人，得先學會那排工具列才看得到工項。
 * 新版只留兩個白話下拉（排序、只看），清單一律是全站通用的清單列（RowButton）。
 *
 * 被收斂掉的常數與函式都留在這個檔案最底下並註明「暫不使用」（硬規則 8），
 * 要放回工具列時把它們接回來即可，不必再推導一次。
 */

/* ===================== 取值與格式 ===================== */

/*
 * 以下 typeLabelOf / isSameDay / daysTo / isLate / isDueToday / dueLabel / STATUS_TAG /
 * tagOf / ElRow 與 pages/Task/CrossList.js 的同名實作**逐字一致**，
 * 是刻意的複製而不是另一套規則：兩支都是清單列的產地，欄位順序與徽章規則必須完全一樣。
 * 抽成共用元件會動到已經上線的頁面，所以照抄並註明來源；之後誰要抽共用元件，一起搬走即可。
 *
 * pages/MyTask/Index.js 的同一套也已經補上 isDueToday 的 done/closed 守門，
 * 三份目前是一致的。要改徽章規則或欄位順序請三份一起改，不要只動一邊。
 */

/**
 * 型別徽章的文字。
 *
 * /Task/List 回得出 issue_type_key，所以這一頁的徽章多半印得出來；
 * 但欄位缺漏時 typeKey() 一律退回 "task"，整排標成「工作」比沒有徽章更誤導，
 * 因此只有原始資料真的帶了型別欄位才貼。
 */
const TYPE_FIELD_KEYS = ["issue_type_key", "issueTypeKey", "issue_type", "IssueTypeKey", "type"];
function typeLabelOf(raw) {
  const k = TYPE_FIELD_KEYS.find((f) => _.get(raw, f));
  if (!k) return "";
  return (TYPE[typeKey(_.get(raw, k))] || TYPE.task).label;
}

/**
 * 「今天」一律用當地時區的日界判斷。
 * 後端的 due_date 是 DateTimeOffset，直接比 UTC 的話，台北時間今天早上 7 點以前
 * 到期的工項會被算成昨天。dayjs 的 isSame(…, "day") 走瀏覽器當地時區。
 */
function isSameDay(v, base) {
  if (!v) return false;
  const d = dayjs(v);
  return d.isValid() && d.isSame(base, "day");
}

/**
 * 距今天的天數（負數＝已經過去）。只比日期，不比時分秒。
 * 這一頁沒有「七天內要交」那個篩選，所以暫時沒有呼叫端 ——
 * 留著是為了讓三份拷貝（Today／CrossList／這裡）保持一字不差，
 * 之後誰要抽成共用元件，三邊才對得起來。
 */
// eslint-disable-next-line no-unused-vars
function daysTo(v, base) {
  if (!v) return null;
  const d = dayjs(v);
  if (!d.isValid()) return null;
  return d.startOf("day").diff(base, "day");
}

/**
 * 真的逾期 ＝ 到期日已經過了「今天」這一天。
 *
 * 不能直接用 normalizeTask 的 overdue，也不能用後端的 is_overdue：
 * 兩邊都是 `due_date < 現在時刻`，而 due_date 存的是當天 00:00，
 * 所以今天到期的工項在今天早上八點就會被判成逾期 —— 摘要卡寫「今天有 3 件要交」、
 * 底下三列卻全部標紅說已經遲了，使用者從此不再相信那個紅字。
 */
function isLate(t, base) {
  return !!t.overdue && !isSameDay(t.dueRaw, base);
}

/**
 * 今天到期 ＝ 到期日就是今天、而且這件事還沒結束。
 *
 * 「還沒結束」要自己擋：isLate 走的是 normalizeTask 的 overdue，
 * 而 overdue 在 design/domain.js 的 isOverdue 開頭就排除了 done/closed；
 * 「今天到期」卻是直接比日期，不擋的話今天做完、到期日剛好是今天的工項
 * 會被標紅⚠「今天要交」，連狀態徽章「完成 ✓」都被期限徽章搶掉，
 * 而「⋯ 更多」裡摘要卡的「今天要交 N 件」是從未完成的工項算的，兩個數字對不起來。
 * 守門寫法與 pages/Schedule/Week.js 的 tagOf 一致。
 */
function isDueToday(t, base) {
  const k = statusKey(t.status);
  return k !== "done" && k !== "closed" && isSameDay(t.dueRaw, base);
}

/** 到期日文案：今天補上「今天」兩個字，今年寫「9 月 30 日」，跨年才補年份 */
function dueLabel(v, base) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  if (d.isSame(base, "day")) return `今天 ${d.format("M/D")}`;
  if (d.isSame(base, "year")) return d.format("M 月 D 日");
  return d.format("YYYY 年 M 月 D 日");
}

/** 狀態 → StatusChip 的 kind 與前綴符號（符號照設計稿：進行中 ●、完成 ✓、關閉 ―） */
const STATUS_TAG = {
  pending: { kind: "closed", mark: <MdRemove /> },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" /> },
  review: { kind: "warn", mark: <MdErrorOutline /> },
  done: { kind: "done", mark: <MdCheck /> },
  closed: { kind: "closed", mark: <MdRemove /> },
};

/**
 * 右側徽章：逾期與今天到期一律搶下 attention，其餘才顯示狀態。
 * 一列只能講一件事，狀態（進行中）跟期限（今天要交）同時出現的話，
 * 真正要看的那個會被稀釋掉。
 */
function tagOf(t, base) {
  if (isDueToday(t, base)) return { kind: "attention", mark: <MdWarningAmber />, text: "今日到期" };
  if (isLate(t, base)) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  const k = statusKey(t.status);
  const s = STATUS_TAG[k] || STATUS_TAG.pending;
  return { kind: s.kind, mark: s.mark, text: (STATUS[k] || STATUS.pending).label };
}

/* ===================== 共用零件 ===================== */

/**
 * 全系統通用的清單列（設計稿 L814-L823）：型別徽章＋標題在上，負責／要交／里程碑在下，
 * 右側是狀態徽章與 ›。整列是按鈕：可點區域就是整列（遠大於 44px）而且帶文字，
 * 不是一顆看不出是什麼的圖示（硬規則 3）。
 */
function ElRow({ attention, typeLabel, title, metas, tag, onClick }) {
  return (
    <RowButton
      tone={attention ? "attention" : ""}
      data-tour="item-card"
      onClick={onClick}
      title={
        <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          {typeLabel ? (
            <StatusChip kind="type" size="small">
              {typeLabel}
            </StatusChip>
          ) : null}
          <span>{title}</span>
        </Box>
      }
      meta={
        metas.length > 0
          ? metas.map((m, i) => (
              <span key={`${i}-${m}`}>{m}</span>
            ))
          : null
      }
      side={
        <StatusChip kind={tag.kind} mark={tag.mark}>
          {tag.text}
        </StatusChip>
      }
    />
  );
}

ElRow.propTypes = {
  attention: PropTypes.bool,
  typeLabel: PropTypes.string,
  title: PropTypes.string.isRequired,
  metas: PropTypes.array,
  tag: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
ElRow.defaultProps = { attention: false, typeLabel: "", metas: [], onClick: () => {} };

/* ===================== 篩選與排序 ===================== */

const ALL = "all";
const ME = "me";

/** 排序下拉的三個選項，用字照設計稿 L800-L804（不是「排序：截止日」這種欄位名） */
const SORTS = [
  { v: "due", label: "依截止日（近到遠）" },
  { v: "attention", label: "依異常狀況優先" },
  { v: "assignee", label: "依負責人" },
];

/** 先畫幾列；其餘由清單底的「看其他 N 件」逐段追加（設計稿 L855） */
const FIRST = 8;
const MORE = 20;

/**
 * 排序用的分級。
 *
 * 「最近要交的排前面」把已完成／已關閉沉到最後：做完的工項沒有要交的東西，
 * 混在前面只會把還要動的那幾件擠出第一屏（專案跑久了，做完的永遠比沒做完的多）。
 */
function dueRank(t, today, closedLast) {
  if (closedLast && (t.status === "done" || t.status === "closed")) return 5;
  if (isLate(t, today)) return 0;
  if (isSameDay(t.dueRaw, today)) return 1;
  return t.dueRaw ? 2 : 3;
}

/** 「有問題的排前面」：已經來不及的 → 今天到期 → 在等人審 → 其他 → 結案的沉底 */
function troubleRank(t, today) {
  if (t.status === "done" || t.status === "closed") return 5;
  if (isLate(t, today)) return 0;
  if (isSameDay(t.dueRaw, today)) return 1;
  if (t.status === "review") return 2;
  return 3;
}

/* ===================== 畫面 ===================== */

function DetailItems({ tasks, ready, me, canCreateTask, onAddTask, onImport, phaseNameOf, onOpenTask }) {
  const [sort, setSort] = useState("due");
  const [who, setWho] = useState(ALL);
  const [shown, setShown] = useState(FIRST);

  /* 今天的日界。用字串當依賴而不是 dayjs 物件：後者每次 render 都是新物件 */
  const todayKey = dayjs().format("YYYY-MM-DD");
  const today = useMemo(() => dayjs(todayKey).startOf("day"), [todayKey]);

  /*
   * 「只看」的人員選項由這個專案的工項推導。
   * 沒有工項的成員不會出現在這裡 —— 選了也是空清單，而「這個專案裡誰有哪些工項」
   * 本來就是這一頁要回答的問題（要看全部成員請到「成員」分頁，?tab=members）。
   */
  const people = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      if (t.assigneeAccount) map.set(t.assigneeAccount, t.assignee || t.assigneeAccount);
    });
    return [...map.entries()]
      .map(([account, name]) => ({ account, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "zh-Hant"));
  }, [tasks]);

  const rows = useMemo(() => {
    let out = tasks;
    if (who === ME) out = out.filter((t) => me && String(t.assigneeAccount || "").toLowerCase() === String(me).toLowerCase());
    else if (who !== ALL) out = out.filter((t) => String(t.assigneeAccount || "") === who);

    const byDue = (a, b) => {
      if (a.dueRaw && b.dueRaw) return dayjs(a.dueRaw).valueOf() - dayjs(b.dueRaw).valueOf();
      return 0;
    };

    if (sort === "assignee") {
      /* 未指派的排最後：那一堆是「還沒決定誰做」，不是某個叫做空字串的人 */
      return [...out].sort((a, b) => {
        const an = a.assignee || "";
        const bn = b.assignee || "";
        if (!an !== !bn) return an ? -1 : 1;
        return an.localeCompare(bn, "zh-Hant") || dueRank(a, today, false) - dueRank(b, today, false) || byDue(a, b);
      });
    }
    if (sort === "attention") {
      return [...out].sort((a, b) => troubleRank(a, today) - troubleRank(b, today) || byDue(a, b));
    }
    return [...out].sort((a, b) => dueRank(a, today, true) - dueRank(b, today, true) || byDue(a, b));
  }, [tasks, who, me, sort, today]);

  /* 換了條件就收回第一段：上一個條件看到第 60 筆，不代表新條件也要從第 60 筆開始 */
  useEffect(() => {
    setShown(FIRST);
  }, [sort, who]);

  const visible = rows.slice(0, shown);
  const rest = rows.length - visible.length;

  return (
    <>
      {/* 工具列只留兩個白話下拉（設計稿 L797-L811）。下拉寬度跟著內容走，不撐滿整行 */}
      {tasks.length > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          data-tour="items-filter"
          sx={{ flexWrap: "wrap", alignItems: "center", py: "var(--space-4)" }}
        >
          <Typography component="label" variant="body2" color="text.secondary" htmlFor="pd-sort">
            排序：
          </Typography>
          <TextField
            select
            id="pd-sort"
            size="small"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            slotProps={{ select: { native: true } }}
            sx={{ width: "auto" }}
          >
            {SORTS.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </TextField>

          <Typography component="label" variant="body2" color="text.secondary" htmlFor="pd-who">
            負責人：
          </Typography>
          <TextField
            select
            id="pd-who"
            size="small"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            slotProps={{ select: { native: true } }}
            sx={{ width: "auto" }}
          >
            <option value={ALL}>全部成員</option>
            {/* 認不出自己的帳號時（token 壞了）就不放這個選項：選了會篩成 0 筆，看起來像壞掉 */}
            {me ? <option value={ME}>我負責的</option> : null}
            {people.map((p) => (
              <option key={p.account} value={p.account}>
                {p.name}
              </option>
            ))}
          </TextField>
        </Stack>
      )}

      {!ready ? (
        <SkeletonRows rows={4} label="工項載入中" />
      ) : tasks.length === 0 ? (
        /*
         * 空的區塊不要畫出來，整塊換成空狀態（硬規則 2）。
         * 新增的入口就放在空狀態裡，跟文件分頁的「上傳第一份文件」、里程碑的「加第一個里程碑」
         * 同一個作法 —— 以前這裡刻意不放、要人去按上面摘要卡的主鈕，但摘要卡現在收在「⋯ 更多」裡，
         * 不展開就看不到，空狀態叫人「用上面的新增工項」就變成指向一顆看不見的按鈕。
         * 「更多」裡也有一顆新增工項，兩個入口叫的是同一支（Detail.js 的 addTask），不會各做一套。
         * 用次要按鈕不用主要按鈕：跟文件分頁的空狀態一致，這一頁的主鈕也不在這裡。
         */
        <Card component="section">
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Typography variant="h2" component="p">
              此專案尚無工項
            </Typography>
            {/* 沒有新增權限的人不放按鈕，也不叫他去按 —— 那兩顆不會顯示給他 */}
            <Typography sx={{ mt: 1 }}>
              {canCreateTask
                ? "可逐筆新增，或透過 Excel 批次建立。"
                : "專案成員新增工項後，將顯示於此。"}
            </Typography>
            {canCreateTask && (
              <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                sx={{ flexWrap: "wrap", alignItems: "center", justifyContent: "center", mt: 3 }}
              >
                <Button variant="outlined" onClick={onAddTask}>
                  新增工項
                </Button>
                <Button variant="text" onClick={onImport}>
                  從 Excel 匯入工項
                </Button>
              </Stack>
            )}
          </Box>
        </Card>
      ) : rows.length === 0 ? (
        <Card component="section">
          <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
            <Typography variant="h2" component="p">
              沒有符合條件的工項
            </Typography>
            <Typography sx={{ mt: 1 }}>將上方「負責人」改為「全部成員」即可檢視此專案的所有工項。</Typography>
          </Box>
        </Card>
      ) : (
        <Card component="section">
          <List disablePadding>
            {visible.map((t) => {
              const phase = phaseNameOf(t.phaseId);
              return (
                <ElRow
                  key={t.id ?? t.code}
                  /* 整列的紅色色帶與右側徽章共用同一組判斷（isDueToday / isLate）——
                     兩邊各寫一份的話，只補其中一邊就會變成「徽章對了、整列還是紅的」 */
                  attention={isDueToday(t, today) || isLate(t, today)}
                  typeLabel={typeLabelOf(t.raw)}
                  title={t.name || "—"}
                  /*
                   * 欄位順序與 /Today、/Tasks 一字不差：負責 → 要交 → 第三格。
                   * 第三格在那兩頁是專案名，這裡整頁都在同一個專案裡，所以放里程碑 ——
                   * 沒綁里程碑就整格不畫，不要填「未分里程碑」充版面（硬規則 10）。
                   */
                  metas={[
                    `負責人：${t.assignee || "未指派"}`,
                    t.dueRaw ? `截止：${dueLabel(t.dueRaw, today)}` : "",
                    phase || "",
                  ].filter(Boolean)}
                  tag={tagOf(t, today)}
                  onClick={() => onOpenTask(t)}
                />
              );
            })}
          </List>

          {/* 「看其他 N 件」（設計稿 L855）：卡底動作列，上方一條分隔線 */}
          {rest > 0 ? (
            <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
              <Button variant="text" onClick={() => setShown((n) => n + MORE)}>
                {rest <= MORE ? `顯示更多（${rest}）` : `顯示更多（${MORE}，尚有 ${rest} 件）`}
              </Button>
            </Box>
          ) : null}
        </Card>
      )}

      {/*
        清單底下的「＋ 新增…」文字入口，跟文件分頁的「＋ 上傳文件」、成員分頁的「＋ 加人進來」
        同一個樣子（DetailData.js 的 AddLink：文字按鈕、上方留一段間距）。
        五顆分頁的新增入口都在自己的內容裡，工項不再是唯一要先展開「⋯ 更多」才加得了東西的分頁。
        「只看」篩到 0 筆時照樣放：篩選不影響「這個專案可以再開一件」。
        還沒有工項時不放 —— 空狀態裡已經有同樣兩顆，同一塊畫面不要重複。
      */}
      {ready && tasks.length > 0 && canCreateTask && (
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: "var(--space-4)" }}>
          <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={onAddTask}>
            新增工項
          </Button>
          <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={onImport}>
            從 Excel 匯入工項
          </Button>
        </Stack>
      )}
    </>
  );
}

DetailItems.propTypes = {
  /** 已經 normalizeTask 過、而且只屬於這個專案的工項 */
  tasks: PropTypes.array,
  /** 工項請求回來了沒 —— 和「一件都沒有」要分開，否則每次進來都先閃一下空狀態 */
  ready: PropTypes.bool,
  /** 目前登入者帳號；取不到就不提供「我負責的」選項 */
  me: PropTypes.string,
  /** 有沒有 task.create —— 沒有就不放新增與匯入的入口，空狀態也不叫他去按 */
  canCreateTask: PropTypes.bool,
  /** 新增工項（Detail.js 導到 /Task/New?projectId=） */
  onAddTask: PropTypes.func,
  /** 從 Excel 匯入工項（Detail.js 導到 /Project/Import/:id） */
  onImport: PropTypes.func,
  /** 里程碑 id → 名稱 */
  phaseNameOf: PropTypes.func,
  onOpenTask: PropTypes.func,
};

DetailItems.defaultProps = {
  tasks: [],
  ready: false,
  me: "",
  canCreateTask: false,
  onAddTask: () => {},
  onImport: () => {},
  phaseNameOf: () => "",
  onOpenTask: () => {},
};

export default DetailItems;

/* ===================== 暫不使用（被收斂掉的舊工具列） =====================
 *
 * 硬規則 8：不准刪功能。下面這幾組是舊「工項管理」分頁的狀態膠囊、8 種排序、
 * 升降冪方向與 7 欄表格用的欄寬，現在畫面上都沒有入口，但推導過程（為什麼是這個
 * 順序、為什麼某個排序要配某個方向）留著才不必再想一次。
 *
 * 要放回去的話：把 STATUS_KEYS 接回膠囊、SORT_OPTS/SORT_DIR 接回排序下拉即可；
 * ITEM_COLS 那張表格則已經被清單列取代（窄螢幕會被切掉右邊、大字模式擠爛，
 * 這也是當初換掉它的理由），要復原請連同橫向捲軸一起評估。
 * ============================================================================ */

/* eslint-disable no-unused-vars */

/** 舊狀態膠囊的五個狀態；膠囊的顯示順序照工作流由左到右 */
const STATUS_KEYS = ["pending", "progress", "review", "done", "closed"];

/*
 * 使用者沒自己點狀態膠囊時，停在這個順序裡第一個有工項的狀態。
 * 手上正在做的最常看，所以進行中優先；全部做完的專案才不會一進來就是一格空的「進行中 0」。
 */
const AUTO_STATUS_ORDER = ["progress", "review", "pending", "done", "closed"];

/*
 * 舊的 8 種排序。預設是建立時間、新的在前；階層排序保留給要看父子結構的人。
 * 沒有「排序：狀態」：清單一次只列一種狀態，照狀態排等於沒排。
 */
const SORT_OPTS = [
  ["created", "排序：建立時間"],
  ["default", "排序：階層"],
  ["due", "排序：截止日"],
  ["start", "排序：開始日"],
  ["prio", "排序：優先級"],
  ["score", "排序：分數"],
  ["name", "排序：名稱"],
  ["assignee", "排序：指派人"],
].map(([value, label]) => ({ value, label }));

/*
 * 各排序鍵的預設方向。建立時間、優先級、分數是「大的在前」才有用；截止日、開始日要先看最近的，
 * 名稱、指派人照字母順。階層排序沒有方向（固定父在前、子緊隨）。
 */
const SORT_DIR = {
  created: "desc",
  prio: "desc",
  score: "desc",
  due: "asc",
  start: "asc",
  name: "asc",
  assignee: "asc",
  default: "asc",
};

/*
 * 舊列表檢視的欄位網格（工項／指派人／狀態／優先級／期間／分數／操作）。
 * min-width = 欄寬 902 + 列的左右 padding 28（全域 border-box）。
 */
const ITEM_COLS =
  "display:grid;grid-template-columns:minmax(240px,1fr) 118px 92px 64px 172px 48px 96px;align-items:center;gap:12px;min-width:930px;";

/**
 * 依 parent_task_id 做 DFS 排序（父在前、子緊隨）——「排序：階層」用的。
 * 後端回傳是平的，所以要自己排；迴圈或孤兒資料仍要被顯示，避免整筆消失。
 */
function hierarchical(list) {
  const byParent = {};
  list.forEach((t) => {
    const p = t.parentId || 0;
    (byParent[p] = byParent[p] || []).push(t);
  });
  const idSet = new Set(list.map((t) => t.id));
  const ordered = [];
  const seen = new Set();
  const visit = (t) => {
    if (seen.has(t.id)) return;
    seen.add(t.id);
    ordered.push(t);
    (byParent[t.id] || []).slice().sort((a, b) => a.id - b.id).forEach(visit);
  };
  list
    .filter((t) => !t.parentId || !idSet.has(t.parentId))
    .sort((a, b) => a.id - b.id)
    .forEach(visit);
  list.forEach((t) => {
    if (!seen.has(t.id)) ordered.push(t);
  });
  return ordered;
}

/* eslint-enable no-unused-vars */
